/**
 * TrulyHungry Theme - Main JavaScript
 * Cart state management, AJAX cart, section rendering, and UI interactions.
 */

/* ============================================
   CART STATE MANAGER (Pub/Sub Pattern)
   ============================================ */
class CartState {
  constructor() {
    this._subscribers = {};
    this._state = {
      items: [],
      item_count: 0,
      total_price: 0,
      subtotal_price: 0,
      currency: 'INR',
    };
    this.fetchCart();
  }

  get state() { return this._state; }

  subscribe(event, callback) {
    if (!this._subscribers[event]) this._subscribers[event] = [];
    this._subscribers[event].push(callback);
  }

  publish(event, data) {
    if (this._subscribers[event]) {
      this._subscribers[event].forEach(cb => cb(data));
    }
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await res.json();
      this._state = data;
      this.publish('cart:updated', data);
      return data;
    } catch (e) {
      // fetchCart error
    }
  }

  async addItem(formData, openDrawer = true) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams(formData),
      });
      if (!res.ok) {
        let err;
        try { err = await res.json(); } catch(e) { err = { message: 'Add to cart failed' }; }
        let errMsg = err.description || err.message || 'Could not add item';
        // Use Shopify's message if it already contains stock info, otherwise use a friendly default
        if (res.status === 422 || errMsg.toLowerCase().includes('stock')) {
           if (errMsg.toLowerCase().includes('items were added')) {
              // use current msg
           } else {
              errMsg = "The maximum quantity of this item is already in your cart.";
           }
        }
        throw new Error(errMsg);
      }
      const item = await res.json();
      await this.fetchCart();
      if (openDrawer) {
        this.publish('cart:item_added', item);
      }
      return item;
    } catch (e) {
      this.publish('cart:error', e.message);
      throw e;
    }
  }

  async changeItem(id, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id, quantity }),
      });
      let data;
      try {
        data = await res.json();
      } catch (e) {
        data = { message: 'Request failed' };
      }

      if (!res.ok) {
        const errorMsg = data.description || data.message || 'Limit reached';
        this.publish('cart:error', errorMsg);
        throw new Error(errorMsg);
      }

      // Silent error check: Some Shopify stores return 200 OK but don't update quantity if stock is limited
      if (quantity > 0 && data.items) {
        // Handle potential variant ID as number vs key as string
        const item = data.items.find(i => i.key === id || i.id === id || i.variant_id === parseInt(id));
        if (item && item.quantity < quantity) {
          const errorMsg = `Only ${item.quantity} items are available in stock.`;
          this.publish('cart:error', errorMsg);
          // Still update state to the 'actual' state, but notify of the error
          this._state = data;
          this.publish('cart:updated', data);
          return data;
        }
      }

      this._state = data;
      this.publish('cart:updated', data);
      return data;
    } catch (e) {
      throw e;
    }
  }

  async removeItem(id) {
    return this.changeItem(id, 0);
  }

  async applyDiscount(code) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: code })
      });
      const data = await res.json();
      this._state = data;
      this.publish('cart:updated', data);
      return data;
    } catch (e) {
      throw e;
    }
  }
}

/* Global cart state instance */
window.TH = window.TH || {};
window.TH.cart = new CartState();

window.TH.cart.subscribe('cart:error', (msg) => {
  if (msg && typeof msg === 'string') {
    if (window.Toast) {
       // Only replace with generic message if it doesn't already have specific stock info
       let finalMsg = msg;
       if (!msg.includes('items were added') && !msg.includes('Only ') && (msg.toLowerCase().includes('stock') || msg.toLowerCase().includes('quantity') || msg.toLowerCase().includes('limit') || msg.toLowerCase().includes('add more'))) {
          finalMsg = "The maximum quantity of this item is already in your cart.";
       }
       window.Toast.show(finalMsg, 'error');
    }
  }
});

window.TH.spinnerSvg = '<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0c5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

window.TH.formatMoney = function(cents, format) {
  if (typeof cents === 'string') { cents = cents.replace(/[^0-9.]/g, ''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || window.TH_settings?.money_format || '₹{{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = (precision === undefined) ? 2 : precision;
    thousands = (thousands === undefined) ? ',' : thousands;
    decimal   = (decimal === undefined)   ? '.' : decimal;

    if (isNaN(number) || number == null) { return 0; }

    number = (number / 100.0).toFixed(precision);

    var parts   = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d)+\d$)/g, '$1' + thousands),
        cents   = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  var match = formatString.match(placeholderRegex);
  if (!match) return formatString; // Fallback if no placeholder

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};


window.TH.buyNow = async function(variantId, quantity, btn) {
  if (!variantId || variantId === '0') return;
  
  // Prevent double clicks
  if (btn && btn.hasAttribute('disabled')) return;
  
  /* ── Prevent adding if technically out of stock ── */
  if (btn && btn.hasAttribute('data-inv-qty')) {
    const invQty = parseInt(btn.dataset.invQty || '0');
    const invPolicy = btn.dataset.invPolicy || 'deny';
    
    // We ignore what's currently in the cart because Buy Now will clear it
    if (invPolicy === 'deny' && parseInt(quantity) > invQty) {
      if (window.Toast) window.Toast.show("Sorry, there is no more stock available.", 'error');
      return;
    }
  }
  
  const originalText = btn ? (btn.getAttribute('data-original-text') || btn.innerHTML) : '';
  const originalDisplay = btn ? (btn.style.display || '') : '';

  if (btn) {
    if (!btn.hasAttribute('data-original-text')) {
      btn.setAttribute('data-original-text', btn.innerHTML);
    }
    btn.setAttribute('data-loading', 'true');
    btn.disabled = true;
    btn.innerHTML = (window.TH && window.TH.spinnerSvg) ? window.TH.spinnerSvg : '…';
    btn.style.display = 'flex';
    btn.style.alignItems = 'center';
    btn.style.justifyContent = 'center';
  }

  try {

    
    const vId = parseInt(variantId);
    if (!vId || isNaN(vId)) throw new Error('Invalid variant selected');

    // 1. BACKUP (Silent)
    try {
      if (window.TH && window.TH.cart && window.TH.cart.state && window.TH.cart.state.item_count > 0) {
        const backup = {
          timestamp: Date.now(),
          items: window.TH.cart.state.items.reduce((acc, i) => {
            acc[i.variant_id] = i.quantity;
            return acc;
          }, {})
        };
        sessionStorage.setItem('th_cart_backup', JSON.stringify(backup));

      }
    } catch(err) { /* Backup failed */ }

    // 2. ISOLATE: Clear synchronously

    await fetch('/cart/clear.js', { 
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    
    // Add a small delay to allow Shopify backend to process the clear
    await new Promise(r => setTimeout(r, 150));
    
    // 3. ADD: Use JSON as it is more robust

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: vId,
        quantity: parseInt(quantity) || 1
      })
    });

    if (!addRes.ok) {
      const errData = await addRes.json();
      throw new Error(errData.description || errData.message || 'Add to cart failed');
    }


    window.location.href = '/checkout';
    
  } catch (e) {
    if (window.Toast) window.Toast.show(e.message || 'Error starting checkout', 'error');
    
    // Reset button immediately on error
    if (btn) {
      btn.innerHTML = btn.getAttribute('data-original-text') || originalText;
      btn.style.display = originalDisplay;
      btn.disabled = false;
      btn.removeAttribute('disabled');
      btn.setAttribute('data-loading', 'false');
    }
  } finally {
    // Safety timeout to reset button if redirect takes too long or fails silently
    if (btn) {
      setTimeout(() => {
        if (btn.getAttribute('data-loading') === 'true') {
          btn.innerHTML = btn.getAttribute('data-original-text') || originalText;
          btn.disabled = false;
          btn.removeAttribute('disabled');
          btn.setAttribute('data-loading', 'false');
        }
      }, 5000);
    }
  }
};

/* ============================================
   TOAST NOTIFICATIONS
   ============================================ */
class Toast {
  static container = null;

  static init() {
    Toast.container = document.querySelector('.toast-container');
    if (!Toast.container) {
      Toast.container = document.createElement('div');
      Toast.container.className = 'toast-container';
      document.body.appendChild(Toast.container);
    }
  }

  static show(message, type = 'success', duration = 3500) {
    if (!Toast.container) Toast.init();
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.innerHTML = `
      <div class="toast-icon" aria-hidden="true">
        ${type === 'error'
          ? '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>'
          : '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'
        }
      </div>
      <span class="toast-text">${message}</span>
      <button class="toast-close" aria-label="Dismiss notification">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>
    `;

    const remove = () => {
      if (toast.classList.contains('removing')) return;
      toast.classList.add('removing');
      setTimeout(() => toast.remove(), 300);
    };

    const closeBtn = toast.querySelector('.toast-close');
    closeBtn.addEventListener('click', remove);

    Toast.container.appendChild(toast);

    if (duration > 0) {
      setTimeout(remove, duration);
    }
  }
}

/* Ensure Toast is globally available */
window.Toast = Toast;
window.addEventListener('DOMContentLoaded', () => Toast.init());

/* ============================================
   CART DRAWER
   ============================================ */
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('th-cart-panel') || document.querySelector('.cart-drawer');
    this.overlay = document.getElementById('th-cart-overlay') || document.querySelector('.cart-overlay');
    this.countBadges = document.querySelectorAll('.cart-count-badge, .cart-count, #cart-count, [data-cart-count]');
    this.isOpen = false;

    if (!this.drawer) {
      return;
    }

    // Check if drawer has required elements before subscribing
    const body = this.drawer.querySelector('.cart-drawer-body');
    const footer = this.drawer.querySelector('.cart-drawer-footer');
    if (!body || !footer) {
      return;
    }
    
    this._bindEvents();
    this._subscribeToCart();
    window.TH.cart.fetchCart();
  }

  _bindEvents() {
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });
    this.overlay?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });
    document.querySelector('.cart-close-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.drawer.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.cart-remove-btn');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent multiple clicks
        if (removeBtn.disabled) return;
        
        // Visual feedback: loading state
        const origHTML = removeBtn.innerHTML;
        const origDisabled = removeBtn.disabled;
        
        removeBtn.innerHTML = window.TH.spinnerSvg;
        removeBtn.disabled = true;
        
        const id = removeBtn.dataset.lineId;
        
        // Execute removal with proper error handling
        this._removeItem(id)
          .catch((err) => {
            // On error, restore the button so the user can try again
            removeBtn.innerHTML = origHTML;
            removeBtn.disabled = origDisabled;
          });
      }
    });

    this.drawer.addEventListener('change', (e) => {
      const input = e.target.closest('.qty-input');
      if (input) {
        const id = input.dataset.lineId;
        const qty = parseInt(input.value);
        if (qty >= 0) {
          this._updateQuantity(id, qty).catch(() => {
            // Error handled by global cart:error subscriber
          });
        }
      }
    });

    this.drawer.addEventListener('click', (e) => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;
      const input = btn.closest('.quantity-selector').querySelector('.qty-input');
      const id = input.dataset.lineId;
      let qty = parseInt(input.value);
      if (btn.dataset.action === 'plus') qty++;
      else qty = Math.max(0, qty - 1);
      
      if (!id) return;

      // Disable all quantity buttons in drawer during update
      const allBtns = this.drawer.querySelectorAll('.qty-btn, .cart-remove-btn');
      allBtns.forEach(b => { 
        b.disabled = true;
        b.style.opacity = '0.5';
        b.style.pointerEvents = 'none';
      });

      input.value = qty;
      this._updateQuantity(id, qty).finally(() => {
        allBtns.forEach(b => {
          b.disabled = false;
          b.style.opacity = '';
          b.style.pointerEvents = '';
        });
      }).catch(() => {
        // Handled by global cart:error subscriber
      });
    });
  }

  _subscribeToCart() {
    window.TH.cart.subscribe('cart:updated', (data) => {
      this._updateCount(data.item_count);
      this._renderItems(data);
    });
    window.TH.cart.subscribe('cart:item_added', (item) => {
      if (window.Toast) Toast.show(`${item.title.toUpperCase()} Added to Cart!`);
      this.open();
    });
  }

  _updateCount(count) {
    // Re-query badges in case they weren't in DOM at constructor time
    const badges = document.querySelectorAll('.cart-count-badge, .cart-count, #cart-count, [data-cart-count]');
    badges.forEach(badge => {
      badge.textContent = count;
      // Force visibility - use important to override any CSS
      if (count === 0) {
        badge.style.cssText = 'display: none !important;';
      } else {
        badge.style.cssText = 'display: flex !important;';
      }
    });
  }

  _renderItems(cart) {
    const body = this.drawer.querySelector('.cart-drawer-body');
    const footer = this.drawer.querySelector('.cart-drawer-footer');

    // Check if drawer elements exist before trying to modify them
    if (!body || !footer) {
      return;
    }

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          </div>
          <h3>${window.TH_strings?.cart_empty || 'Your Cart is Empty'}</h3>
          <p>${window.TH_strings?.cart_empty_sub || 'Add some delicious snacks!'}</p>
          <a href="/collections/all" class="btn btn-primary btn-sm" onclick="window.TH.cartDrawerInstance.close()">${window.TH_strings?.start_shopping || 'Start Shopping'}</a>
        </div>
      `;
      footer.style.display = 'none';
      return;
    }

    footer.style.display = '';
    body.innerHTML = cart.items.map(item => this._renderItem(item)).join('');
    this._renderFooter(cart);
  }

  _renderItem(item) {
    const hasDiscount = item.original_price > item.final_price;
    return `
      <div class="cart-item" data-item-key="${item.key}">
        <img src="${item.image}" alt="${item.title}" class="cart-item-image" loading="lazy" width="80" height="80">
        <div class="cart-item-info">
          <div class="cart-item-title">${item.product_title}</div>
          ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
          <div class="cart-item-price">
            ${hasDiscount ? `<s>${this._formatMoney(item.original_line_price)}</s> ` : ''}
            ${this._formatMoney(item.final_line_price)}
          </div>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:0.5rem;flex-shrink:0;">
          <button class="cart-remove-btn" data-line-id="${item.key}" aria-label="Remove ${item.title}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
          <div class="quantity-selector">
            <button class="qty-btn" data-action="minus" aria-label="Decrease quantity">−</button>
            <input class="qty-input" type="number" value="${item.quantity}" min="0" data-line-id="${item.key}" aria-label="Quantity">
            <button class="qty-btn" data-action="plus" aria-label="Increase quantity">+</button>
          </div>
          ${item.quantity_error ? `<div class="text-[10px] text-red-500 font-bold uppercase mt-1">The maximum quantity of this item is already in your cart.</div>` : ''}
        </div>
      </div>
    `;
  }

  _renderFooter(cart) {
    const threshold = parseInt(window.TH_settings?.free_shipping_threshold || 50000);
    const pct = Math.min(100, (cart.total_price / threshold) * 100);
    const remaining = threshold - cart.total_price;
    const footer = this.drawer.querySelector('.cart-drawer-footer');

    footer.innerHTML = `
      ${threshold > 0 ? `
        <div class="cart-free-shipping-bar">
          <p class="cart-free-shipping-text">
            ${pct >= 100
              ? (window.TH_strings?.free_shipping_achieved || "You've unlocked free shipping! 🎉")
              : `Add ${this._formatMoney(remaining)} more for free shipping`
            }
          </p>
          <div class="cart-free-shipping-progress">
            <div class="cart-free-shipping-fill" style="width:${pct}%"></div>
          </div>
        </div>
      ` : ''}
      
      ${cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0 ? `
        <div class="cart-discounts" style="margin-bottom: 1rem; border-bottom: 1px solid rgba(0,0,0,0.05); padding-bottom: 0.75rem;">
          ${cart.cart_level_discount_applications.map(disc => `
            <div style="display:flex; justify-content: space-between; align-items: center; font-size: 0.75rem; font-weight: 700; color: #10b981; text-transform: uppercase; letter-spacing: 0.05em;">
              <span>Discount (${disc.title})</span>
              <span>-${this._formatMoney(disc.total_allocated_amount)}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <div class="cart-subtotal" style="display:flex; flex-direction:column; gap:0.25rem;">
        <div style="display:flex; justify-content: space-between; align-items: baseline;">
          <span class="cart-subtotal-label" style="font-weight: 700; color: rgba(var(--color-primary-rgb), 0.6); text-transform: uppercase; letter-spacing: 0.1em; font-size: 0.75rem;">Subtotal</span>
          <div style="display:flex; flex-direction:column; align-items: flex-end;">
            ${cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0 ? `
              <span style="font-size: 0.75rem; color: rgba(var(--color-primary-rgb), 0.3); text-decoration: line-through; font-weight: 700;">
                ${this._formatMoney(cart.total_price + cart.cart_level_discount_applications.reduce((acc, d) => acc + d.total_allocated_amount, 0))}
              </span>
            ` : ''}
            <span class="cart-subtotal-amount" style="font-size: 1.875rem; font-weight: 900; color: var(--color-primary); letter-spacing: -0.025em;">${this._formatMoney(cart.total_price)}</span>
          </div>
        </div>
      </div>
      <a href="/checkout" class="cart-checkout-btn">Checkout →</a>
      <a href="/cart" style="display:block;text-align:center;margin-top:0.75rem;font-size:0.8125rem;color:rgba(6,78,59,0.5);font-family:var(--font-mono-family);font-weight:700;text-transform:uppercase;letter-spacing:0.1em;text-decoration:none;" class="hover-accent">View Cart</a>
    `;
  }

  async _updateQuantity(id, qty) {
    try {
      await window.TH.cart.changeItem(id, qty);
    } catch (e) {
      // Re-render current state to fix UI input if it was changed
      this._renderItems(window.TH.cart.state);
      // Re-throw so click/change handlers can show toast
      throw e;
    }
  }

  async _removeItem(id) {
    try {
      await window.TH.cart.removeItem(id);
    } catch (error) {
      throw error; // Re-throw to be caught by the click handler
    }
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('open');
    this.overlay?.classList.add('open');
    document.body.classList.add('cart-drawer-open');
    document.documentElement.classList.add('cart-drawer-open');
    document.body.style.overflow = 'hidden';
    document.dispatchEvent(new CustomEvent('cart:open'));
    const firstFocusable = this.drawer.querySelector('button, a, input');
    firstFocusable?.focus();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.classList.remove('cart-drawer-open');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  _formatMoney(cents) {
    return window.TH.formatMoney(cents);
  }
}

/* ============================================
   ADD TO CART BUTTONS
   ============================================ */
const ATC_SPINNER_SVG = '<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0c5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';
const ATC_CART_SVG    = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';
const ATC_CHECK_SVG   = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
const ATC_ERROR_SVG   = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

function bindAjaxAtcButtons() {
  if (window._atcBound) return;
  window._atcBound = true;

  /* ── 1. ATC Buttons (Event Delegation) ── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-atc-btn]');
    if (!btn) return;
    
    if (btn.hasAttribute('disabled')) return;
    e.preventDefault();
    e.stopPropagation();

    const variantId = btn.dataset.variantId;
    const qtyInput = btn.closest('form')?.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][data-product-id="${btn.dataset.productId}"]`);
    const qty = parseInt(btn.dataset.qty || qtyInput?.value || '1');

    if (!variantId || variantId === '0') { 
      Toast.show('Please select a variant', 'error'); 
      return; 
    }

    /* ── Prevent adding more than inventory ── */
    if (btn.hasAttribute('data-inv-qty')) {
      const invQty = parseInt(btn.dataset.invQty || '0');
      const invPolicy = btn.dataset.invPolicy || 'deny';
      const currentCart = window.TH.cart ? window.TH.cart.state.items : [];
      const inCart = currentCart.find(i => i.variant_id == variantId);
      const qtyInCart = inCart ? inCart.quantity : 0;
      
      if (invPolicy === 'deny' && (qtyInCart + qty) > invQty) {
        if (window.Toast) window.Toast.show("The maximum quantity of this item is already in your cart.", 'error');
        return;
      }
    }

    /* State variables for feedback */
    const isCollectionBtn = btn.classList.contains('collection-atc');
    const origHTML = btn.innerHTML;
    const origPointerEvents = btn.style.pointerEvents;
    
    /* Visual Feedback: Loading */
    btn.innerHTML = ATC_SPINNER_SVG;
    btn.disabled = true;
    btn.setAttribute('disabled', 'disabled');
    btn.style.pointerEvents = 'none';

    if (!isCollectionBtn) {
      btn.style.opacity = '0.7';
    }

    let success = false;
    let errorMsg = 'Could not add item';

    try {
      await window.TH.cart.addItem({ id: variantId, quantity: qty });
      success = true;
    } catch(err) {
      errorMsg = err.message || errorMsg;
    }

    if (success) {
      /* Visual Feedback: Success */
      btn.innerHTML = ATC_CHECK_SVG;
      
      if (isCollectionBtn) {
        btn.style.background = 'var(--color-accent)';
        btn.style.color = '#fff';
      } else {
        btn.style.opacity = '1';
        btn.style.background = 'rgba(16,185,129,0.9)';
        btn.style.color = '#fff';
      }

      /* Refresh sequences */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = origPointerEvents;
        btn.style.background = '';
        btn.style.color = '';
        btn.style.opacity = '';
      }, 1800);
    } else {
      /* Visual Feedback: Error */
      btn.innerHTML = ATC_ERROR_SVG;
      
      if (isCollectionBtn) {
        btn.style.background = 'var(--color-danger)';
        btn.style.color = '#fff';
      } else {
        btn.style.opacity = '1';
        btn.style.background = 'rgb(var(--color-danger-rgb))';
        btn.style.color = 'var(--color-danger)';
      }

      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = origPointerEvents;
        btn.style.background = '';
        btn.style.color = '';
        btn.style.opacity = '';
      }, 2000);
    }
  });

  /* ── 2. Buy Now Buttons (Event Delegation) ── */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-buy-btn]');
    if (!btn) return;
    
    e.preventDefault();
    e.stopPropagation();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    // Direct to checkout - let Shopify handle cart management
    window.location.href = `/cart/add?id=${variantId}&quantity=1&return_to=/checkout`;
  });
}

/* ============================================
   PRODUCT FORM (Product Page)
   ============================================ */
class ProductForm {
  constructor(formEl) {
    this.form = formEl;
    this.variantData = JSON.parse(document.getElementById('product-variant-json')?.textContent || '[]');
    this.inventoryData = JSON.parse(document.getElementById('product-inventory-data')?.textContent || '{}');
    this.selectedOptions = {};

    this._bindVariantPills();
    this._bindQuantity();
    this._bindAtc();
    this._bindBuyNow();
    this._initStickyBar();
  }

  _bindVariantPills() {
    this.form.querySelectorAll('.variant-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const position = pill.dataset.position;
        const value = pill.dataset.value;

        // Update selected pills in this option group
        this.form.querySelectorAll(`.variant-pill[data-position="${position}"]`).forEach(p => p.classList.remove('active'));
        pill.classList.add('active');

        this.selectedOptions[position] = value;
        this._updateVariant();
      });
    });
  }

  _updateVariant() {
    const options = Object.values(this.selectedOptions);
    this.currentVariant = this.variantData.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
    this._updateUI();
  }

  _updateUI() {
    const atcBtn = this.form.querySelector('[data-atc-btn]');
    const priceEl = document.querySelector('[data-product-price]');
    const comparePriceEl = document.querySelector('[data-product-compare-price]');
    const saveBadge = document.querySelector('[data-product-save-badge]');

    if (!this.currentVariant) {
      if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Select a variant'; }
      return;
    }

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());

    // Update price
    if (priceEl) priceEl.textContent = window.TH.formatMoney(this.currentVariant.price);
    if (comparePriceEl) {
      if (this.currentVariant.compare_at_price > this.currentVariant.price) {
        comparePriceEl.textContent = window.TH.formatMoney(this.currentVariant.compare_at_price);
        comparePriceEl.style.display = '';
        if (saveBadge) {
          const pct = Math.round((1 - this.currentVariant.price / this.currentVariant.compare_at_price) * 100);
          saveBadge.textContent = `-${pct}%`;
          saveBadge.style.display = '';
        }
      } else {
        comparePriceEl.style.display = 'none';
        if (saveBadge) saveBadge.style.display = 'none';
      }
    }

    // Update ATC state
    if (atcBtn) {
      atcBtn.dataset.variantId = this.currentVariant.id;
      
      const inv = this.inventoryData[this.currentVariant.id];
      if (inv) {
        atcBtn.dataset.invQty = inv.qty || '0';
        atcBtn.dataset.invPolicy = inv.policy || 'deny';
      }

      if (!this.currentVariant.available) {
        atcBtn.disabled = true;
        atcBtn.textContent = window.TH_strings?.sold_out || 'Sold Out';
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = window.TH_strings?.add_to_cart || 'Add to Cart';
      }
    }
    
    // Sticky bar
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = window.TH.formatMoney(this.currentVariant.price);
  }

  _bindQuantity() {
    const qtyInput = this.form.querySelector('[name="quantity"]');
    this.form.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        let qty = parseInt(qtyInput.value);
        if (btn.dataset.action === 'plus') qty++;
        else qty = Math.max(1, qty - 1);
        qtyInput.value = qty;
      });
    });
  }

  _bindAtc() {
    const btn = this.form.querySelector('[data-atc-btn]');
    if (!btn) return;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!this.currentVariant) { Toast.show('Please select a variant', 'error'); return; }
      const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1');
      
      /* ── Inventory Guard ── */
      const invCount = window.TH.cart ? window.TH.cart.state.items.find(i => i.variant_id == this.currentVariant.id)?.quantity || 0 : 0;
      const invQty = parseInt(this.form.querySelector('[data-atc-btn]')?.dataset.invQty || '99999');
      const invPolicy = this.form.querySelector('[data-atc-btn]')?.dataset.invPolicy || 'deny';
      if (invPolicy === 'deny' && (invCount + qty) > invQty) {
        if (window.Toast) window.Toast.show("The maximum quantity of this item is already in your cart.", 'error');
        return;
      }

      btn.classList.add('loading');
      btn.disabled = true;
      try {
        await window.TH.cart.addItem({ id: this.currentVariant.id, quantity: qty });
      } catch (_) {}
      btn.classList.remove('loading');
      btn.disabled = !this.currentVariant?.available;
    });
  }

  _bindBuyNow() {
    const btn = this.form.querySelector('[data-buy-now-btn]');
    if (!btn) return;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!this.currentVariant?.available) return;
      const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1');

      /* ── Inventory Guard ── */
      const invCount = window.TH.cart ? window.TH.cart.state.items.find(i => i.variant_id == this.currentVariant.id)?.quantity || 0 : 0;
      const invQty = parseInt(this.form.querySelector('[data-atc-btn]')?.dataset.invQty || '99999');
      const invPolicy = this.form.querySelector('[data-atc-btn]')?.dataset.invPolicy || 'deny';
      if (invPolicy === 'deny' && (invCount + qty) > invQty) {
        if (window.Toast) window.Toast.show("The maximum quantity of this item is already in your cart.", 'error');
        return;
      }

      // Direct to checkout - let Shopify handle cart management
      window.location.href = `/cart/add?id=${this.currentVariant.id}&quantity=${qty}&return_to=/checkout`;
    });
  }

  _initStickyBar() {
    const stickyBar = document.querySelector('.sticky-buy-bar');
    const formEl = this.form;
    if (!stickyBar || !formEl) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          stickyBar.classList.add('visible');
        } else {
          stickyBar.classList.remove('visible');
        }
      });
    }, { threshold: 0 });

    observer.observe(formEl);

    const stickyAtc = stickyBar.querySelector('[data-sticky-atc]');
    if (stickyAtc) {
      stickyAtc.addEventListener('click', async () => {
        if (!this.currentVariant?.available) return;
        stickyAtc.classList.add('loading');
        try {
          await window.TH.cart.addItem({ id: this.currentVariant.id, quantity: 1 });
        } catch (_) {}
        stickyAtc.classList.remove('loading');
      });
    }
  }

  _formatMoney(cents) {
    return window.TH.formatMoney(cents);
  }
}

/* ============================================
   ACCORDION
   ============================================ */
function initAccordions() {
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      const content = document.getElementById(trigger.getAttribute('aria-controls'));

      // Close all in same accordion group
      trigger.closest('.accordion-list')?.querySelectorAll('.accordion-trigger').forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        document.getElementById(t.getAttribute('aria-controls'))?.classList.remove('open');
      });

      if (!isOpen) {
        trigger.setAttribute('aria-expanded', 'true');
        content?.classList.add('open');
      }
    });
  });
}

/* ============================================
   STICKY HEADER
   ============================================ */
function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > 80) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
    lastY = y;
  }, { passive: true });
}

/* ============================================
   MOBILE NAV DRAWER
   ============================================ */
function initMobileNav() {
  const hamburger = document.querySelector('.hamburger-btn');
  const drawer = document.querySelector('.mobile-nav-drawer');
  const overlay = document.querySelector('.mobile-nav-overlay');
  const closeBtn = drawer?.querySelector('.mobile-nav-close');

  const open = () => {
    drawer?.classList.add('open');
    overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    hamburger?.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    drawer?.classList.remove('open');
    overlay?.classList.remove('open');
    document.body.style.overflow = '';
    hamburger?.setAttribute('aria-expanded', 'false');
  };

  hamburger?.addEventListener('click', open);
  overlay?.addEventListener('click', close);
  closeBtn?.addEventListener('click', close);
}

/* ============================================
   PREDICTIVE SEARCH
   ============================================ */
class PredictiveSearch {
  constructor() {
    this.searchInput = document.querySelector('[data-predictive-search-input]');
    this.resultsContainer = document.querySelector('[data-predictive-search-results]');
    this.wrapper = document.querySelector('.search-wrapper');
    this.debounceTimer = null;

    if (!this.searchInput || !this.resultsContainer) return;
    this._bindEvents();
  }

  _bindEvents() {
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);
      const q = e.target.value.trim();
      if (q.length < 2) { this._hideResults(); return; }
      this.debounceTimer = setTimeout(() => this._search(q), 300);
    });

    document.addEventListener('click', (e) => {
      if (!this.wrapper?.contains(e.target)) this._hideResults();
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._hideResults();
    });
  }

  async _search(query) {
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`);
      const data = await res.json();
      this._renderResults(data.resources?.results?.products || []);
    } catch (e) {
    }
  }

  _renderResults(products) {
    if (products.length === 0) {
      this.resultsContainer.innerHTML = `<p style="padding:1rem;font-size:0.875rem;color:rgba(6,78,59,0.5);">No results found</p>`;
    } else {
      this.resultsContainer.innerHTML = products.map(p => `
        <a href="${p.url}" style="display:flex;align-items:center;gap:0.75rem;padding:0.75rem 1rem;text-decoration:none;color:inherit;transition:background 0.2s;border-radius:8px;" onmouseover="this.style.background='rgba(6,78,59,0.04)'" onmouseout="this.style.background=''">
          <img src="${p.featured_image?.url || ''}" alt="${p.title}" width="40" height="40" style="border-radius:8px;object-fit:cover;flex-shrink:0;" loading="lazy">
          <div>
            <div style="font-size:0.875rem;font-weight:700;color:var(--color-primary);">${p.title}</div>
            <div style="font-size:0.75rem;color:var(--color-text-muted);">${window.TH.formatMoney(p.price)}</div>
          </div>
        </a>
      `).join('');
    }
    this.resultsContainer.classList.add('open');
  }

  _hideResults() {
    this.resultsContainer?.classList.remove('open');
  }
}

/* ============================================
   GALLERY (Product Page)
   ============================================ */
function initProductGallery() {
  const mainImg = document.querySelector('.product-gallery-main img');
  document.querySelectorAll('.product-gallery-item').forEach(item => {
    item.addEventListener('click', () => {
      const src = item.querySelector('img')?.src;
      if (mainImg && src) {
        mainImg.src = src;
        document.querySelectorAll('.product-gallery-item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');
      }
    });
  });
}

/* ============================================
   NEWSLETTER FORM
   ============================================ */
function initNewsletterForms() {
  document.querySelectorAll('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('[name="contact[email]"]');
      const errorEl = form.querySelector('[data-newsletter-error]');
      const btn = form.querySelector('[type="submit"]');
      const email = input?.value.trim();

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errorEl) errorEl.textContent = 'Please enter a valid email address';
        input?.focus();
        return;
      }
      if (errorEl) errorEl.textContent = '';

      btn.classList.add('loading');
      try {
        await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { 'Accept': 'application/json' },
        });
        Toast.show('Thanks for subscribing! 🎉');
        form.reset();
      } catch (_) {
        Toast.show('Something went wrong. Please try again.', 'error');
      }
      btn.classList.remove('loading');
    });
  });
}

/* ============================================
   COLLECTION FILTERS
   ============================================ */
function initCollectionFilters() {
  const filterBtns = document.querySelectorAll('[data-filter-btn]');
  const sortSelect = document.querySelector('[data-sort-select]');

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const url = new URL(window.location.href);
      const filter = btn.dataset.filterValue;
      if (filter) {
        url.searchParams.set('filter.p.product_type', filter);
      } else {
        url.searchParams.delete('filter.p.product_type');
      }
      window.location.href = url.toString();
    });
  });

  sortSelect?.addEventListener('change', (e) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    window.location.href = url.toString();
  });
}

/* ============================================
   LIGHTBOX
   ============================================ */
function initLightbox() {
  const lightbox = document.querySelector('.lightbox');
  const lightboxContent = lightbox?.querySelector('.lightbox-content');
  const lightboxClose = lightbox?.querySelector('.lightbox-close');

  document.querySelectorAll('[data-lightbox]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const src = trigger.dataset.src;
      if (lightboxContent && src) {
        lightboxContent.innerHTML = `<img src="${src}" alt="">`;
      }
      lightbox?.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  });

  const closeLightbox = () => {
    lightbox?.classList.remove('open');
    document.body.style.overflow = '';
  };

  lightboxClose?.addEventListener('click', closeLightbox);
  lightbox?.addEventListener('click', (e) => {
    if (e.target === lightbox) closeLightbox();
  });
}

/* ============================================
   ANIMATE ON SCROLL
   ============================================ */
function initScrollAnimations() {
  const elements = document.querySelectorAll('[data-animate]');
  if (!elements.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.animationPlayState = 'running';
        entry.target.style.opacity = '1';
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  elements.forEach(el => {
    el.style.opacity = '0';
    el.style.animationPlayState = 'paused';
    observer.observe(el);
  });
}

/* ============================================
   INIT ON DOM READY
   ============================================ */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();

  // Cart Drawer
  const cartDrawerInst = new CartDrawer();
  window.TH.cartDrawerInstance = cartDrawerInst;

  // Sticky Header
  initStickyHeader();

  // Mobile Nav
  initMobileNav();

  // Predictive Search
  new PredictiveSearch();

  // ATC buttons
  bindAjaxAtcButtons();

  // Product Form
  const productForm = document.querySelector('[data-product-form]');
  if (productForm) new ProductForm(productForm);

  // Gallery
  initProductGallery();

  // Accordions
  initAccordions();

  // Newsletter
  initNewsletterForms();

  // Collection filters
  initCollectionFilters();

  // Lightbox
  initLightbox();

  // Scroll animations
  initScrollAnimations();

  // Active nav link
  const currentPath = window.location.pathname;
  document.querySelectorAll('.header-nav-link').forEach(link => {
    if (link.getAttribute('href') === currentPath) link.classList.add('active');
  });
});
/* ============================================
   CART RESTORER (For Isolated Buy Now)
   ============================================ */
(function() {
  const restoreCart = async () => {
    const backupStr = sessionStorage.getItem('th_cart_backup');
    if (!backupStr) return;

    // Only restore if we are NOT on a checkout or success page
    const path = window.location.pathname;
    if (path.includes('/checkout') || path.includes('/thank_you') || path.includes('/orders/')) {
       // Keep the backup until they actually leave the checkout flow
       return;
    }

    try {
      const backup = JSON.parse(backupStr);
      // Expire backup after 30 minutes
      if (Date.now() - backup.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem('th_cart_backup');
        return;
      }


      
      // Clear current "Buy Now" temporary cart first to ensure isolation
      await fetch('/cart/clear.js', { method: 'POST' });

      // Bulk update is much faster than individual adds
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          updates: backup.items
        })
      });

      // Cleanup
      sessionStorage.removeItem('th_cart_backup');
      
      // Update UI
      if (window.TH && window.TH.cart) {
        await window.TH.cart.fetchCart();
      }
      

    } catch (e) {
    }
  };

  window.addEventListener('pageshow', (event) => {
    // pageshow event fires on back-navigation, even with BFCache (event.persisted will be true)
    restoreCart();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreCart);
  } else {
    restoreCart();
  }
})();
